/* Options → Alert Log sub-tab.
   Lists every Telegram alert fired by the server-side options monitor:
   trade-opened, take profit (50% / 70%), stop loss (40% / 50%), and
   chart-invalidation breaches. Reads from `options_alerts` joined with
   the user's own `options_trades` so every row carries the human-readable
   strategy + strikes + entry premium. Supports filtering by ticker,
   alert type, and date range, plus CSV export. */

import { useEffect, useMemo, useState } from "react";
import { C } from "./_shared";
import { apiJSON } from "./_optionsShared";

// Map raw level codes (from the cron) to UI labels + colors.
const LEVEL_META = {
  tp1:   { label: "Take Profit 50%", color: C.green, badge: C.gb,  text: C.gt },
  tp2:   { label: "Take Profit 70%", color: C.green, badge: C.gb,  text: C.gt },
  sl1:   { label: "Stop Loss 40%",   color: C.amber, badge: C.ab,  text: C.at },
  sl2:   { label: "Stop Loss 50%",   color: C.red,   badge: C.rb,  text: C.rt },
  inval: { label: "Chart Invalid",   color: C.red,   badge: C.rb,  text: C.rt },
};
const ALL_LEVELS = ["all", ...Object.keys(LEVEL_META)];

function fmtDateTime(iso) {
  if (!iso) return { date: "—", time: "—" };
  try {
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" }),
      time: d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }),
    };
  } catch { return { date: "—", time: "—" }; }
}

export default function OptionsAlertLog() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tickerFilter, setTickerFilter] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await apiJSON("/api/trading/options_alerts");
        if (!cancelled) setAlerts(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setAlerts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    return alerts.filter(a => {
      if (tickerFilter && !(a.ticker || "").toUpperCase().includes(tickerFilter.toUpperCase())) return false;
      if (levelFilter !== "all" && a.level !== levelFilter) return false;
      if (fromDate || toDate) {
        const d = (a.created_at || "").slice(0, 10);
        if (fromDate && d < fromDate) return false;
        if (toDate   && d > toDate)   return false;
      }
      return true;
    });
  }, [alerts, tickerFilter, levelFilter, fromDate, toDate]);

  const stats = useMemo(() => {
    const tp = filtered.filter(a => a.level === "tp1" || a.level === "tp2").length;
    const sl = filtered.filter(a => a.level === "sl1" || a.level === "sl2").length;
    const inval = filtered.filter(a => a.level === "inval").length;
    return { tp, sl, inval, total: filtered.length };
  }, [filtered]);

  function exportCSV() {
    const rows = [
      ["Date", "Time", "Ticker", "Strategy", "Alert", "P/L %", "Stock Price", "Buy Strike", "Sell Strike", "Qty"],
      ...filtered.map(a => {
        const { date, time } = fmtDateTime(a.created_at);
        const lvl = LEVEL_META[a.level]?.label || a.level || "";
        return [date, time, a.ticker || "", a.strategy || "", lvl,
          (a.pnl_pct ?? "").toString(), (a.stock_price ?? "").toString(),
          (a.buy_strike ?? "").toString(), (a.sell_strike ?? "").toString(),
          (a.qty ?? "").toString()];
      }),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `options-alerts-${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div data-testid="options-alert-log" style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* Stats strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
        <Stat label="Total Alerts"      value={stats.total} color="#111"/>
        <Stat label="Take Profit Hits"  value={stats.tp}    color={C.green}/>
        <Stat label="Stop Loss Hits"    value={stats.sl}    color={C.amber}/>
        <Stat label="Chart Invalidated" value={stats.inval} color={C.red}/>
      </div>

      {/* Filters */}
      <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "12px 16px",
                    display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 130, flex: "1 1 130px" }}>
          <label style={{ fontSize: 10, color: C.gray, fontWeight: 500 }}>Ticker</label>
          <input type="text" value={tickerFilter} onChange={e => setTickerFilter(e.target.value)}
            placeholder="MSFT" data-testid="options-alert-filter-ticker"
            style={{ padding: "7px 9px", borderRadius: 6, border: "0.5px solid #d1d5db", fontSize: 12, background: "#fff" }}/>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 160, flex: "1 1 160px" }}>
          <label style={{ fontSize: 10, color: C.gray, fontWeight: 500 }}>Alert Type</label>
          <select value={levelFilter} onChange={e => setLevelFilter(e.target.value)}
            data-testid="options-alert-filter-level"
            style={{ padding: "7px 9px", borderRadius: 6, border: "0.5px solid #d1d5db", fontSize: 12, background: "#fff" }}>
            {ALL_LEVELS.map(l => (
              <option key={l} value={l}>{l === "all" ? "All Types" : LEVEL_META[l]?.label || l}</option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 140, flex: "1 1 140px" }}>
          <label style={{ fontSize: 10, color: C.gray, fontWeight: 500 }}>From</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            data-testid="options-alert-filter-from"
            style={{ padding: "7px 9px", borderRadius: 6, border: "0.5px solid #d1d5db", fontSize: 12, background: "#fff" }}/>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 140, flex: "1 1 140px" }}>
          <label style={{ fontSize: 10, color: C.gray, fontWeight: 500 }}>To</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            data-testid="options-alert-filter-to"
            style={{ padding: "7px 9px", borderRadius: 6, border: "0.5px solid #d1d5db", fontSize: 12, background: "#fff" }}/>
        </div>
        <button onClick={() => { setTickerFilter(""); setLevelFilter("all"); setFromDate(""); setToDate(""); }}
          data-testid="options-alert-filter-reset"
          style={{ padding: "8px 14px", borderRadius: 7, border: "0.5px solid #d1d5db", background: "#fff",
                   fontSize: 11, fontWeight: 700, cursor: "pointer", color: C.gray }}>
          Reset
        </button>
        <button onClick={exportCSV} disabled={filtered.length === 0}
          data-testid="options-alert-export-btn"
          style={{ padding: "8px 14px", borderRadius: 7, border: "none",
                   background: filtered.length === 0 ? C.gbg : C.indigo,
                   color: filtered.length === 0 ? C.gray : "#fff",
                   fontSize: 11, fontWeight: 700, cursor: filtered.length === 0 ? "not-allowed" : "pointer" }}>
          ⬇ Export CSV
        </button>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: C.gray, fontSize: 12 }}>Loading alerts…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: C.gray, fontSize: 12 }}>
            {alerts.length === 0
              ? "No alerts fired yet. The server-side monitor runs every 5 minutes during market hours and records each crossing here."
              : "No alerts match the active filters."}
          </div>
        ) : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 900 }}>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  {["Date", "Time", "Ticker", "Strategy", "Alert", "P/L %", "Stock @ Alert", "Strikes"].map(h => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: C.gray, fontWeight: 500, fontSize: 10, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, idx) => {
                  const { date, time } = fmtDateTime(a.created_at);
                  const meta = LEVEL_META[a.level] || { label: a.level, color: C.gray, badge: C.gbg, text: C.gray };
                  const pnl = typeof a.pnl_pct === "number" ? a.pnl_pct : 0;
                  return (
                    <tr key={`${a.trade_id}-${a.level}-${idx}`} data-testid={`options-alert-row-${idx}`}
                        style={{ borderTop: "0.5px solid #f3f4f6" }}>
                      <td style={{ padding: "8px 12px", color: "#111", whiteSpace: "nowrap" }}>{date}</td>
                      <td style={{ padding: "8px 12px", color: C.gray, fontFamily: "monospace", whiteSpace: "nowrap" }}>{time}</td>
                      <td style={{ padding: "8px 12px", fontWeight: 800, color: "#111" }}>{a.ticker || "—"}</td>
                      <td style={{ padding: "8px 12px", color: C.gray, fontSize: 10 }}>{a.strategy || "—"}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 4, background: meta.badge, color: meta.text, fontWeight: 700, whiteSpace: "nowrap" }}>
                          {meta.label}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", fontWeight: 700, color: pnl >= 0 ? C.green : C.red, whiteSpace: "nowrap" }}>
                        {pnl >= 0 ? "+" : ""}{pnl.toFixed(1)}%
                      </td>
                      <td style={{ padding: "8px 12px", color: "#111", fontFamily: "monospace", whiteSpace: "nowrap" }}>
                        ${typeof a.stock_price === "number" ? a.stock_price.toFixed(2) : "—"}
                      </td>
                      <td style={{ padding: "8px 12px", color: C.gray, fontFamily: "monospace", fontSize: 10, whiteSpace: "nowrap" }}>
                        {a.buy_strike && a.sell_strike ? `${a.buy_strike}/${a.sell_strike}` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "12px 14px" }}>
      <div style={{ fontSize: 10, color: C.gray, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 900, color: color || "#111" }}>{value}</div>
    </div>
  );
}
